import moment from 'moment';
import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import PagedResultSet from 'src/models/common/PagedResultSet';
import County from 'src/models/County';
import Assignment from 'src/models/dlcLease/Assignment';
import LeaseRecording from 'src/models/dlcLease/LeaseRecording';
import State from 'src/models/State';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';
import LeaseRecordingsList from './LeaseRecordingsList';
import CollapsibleSection from 'src/components/common/CollapsibleSection';
import Page from 'src/components/common/Page';
import CardContainer from 'src/components/common/CardContainer.redesign';
import _ from 'lodash';
import { ODataFilter, ODataFilterOperators, ODataOptions } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import OwnerSearchResult from 'src/models/OwnerSearchResult';
import Card from 'src/components/common/Card.redesign';
import LeaseGenerationOwnerData from 'src/models/leasing/LeaseGenerationOwnerData';
import OwnerUtil from 'src/util/OwnerUtil';
import CreateOwner from '../../owners/createOwner/CreateOwner';
import IOwner from 'src/models/IOwner';
import Filter from 'src/components/common/Filter';
import DocGenerationComponent from '../../LeasingComponents/DocGenerationComponent';
import DocumentTemplate from 'src/models/DocumentTemplate';
import * as Constants from 'src/components/common/Constants';
import DocTemplateList from 'src/models/common/DocTemplateList';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';

interface IAssignmentDialogStateProps {
    assignment: Assignment;
    states: State[];

    sendUpdate: () => void;
    onCloseDialog?: () => void;
}

const AssignmentDialog = (props: IAssignmentDialogStateProps) => {
    const parentListTableConfig: ITableConfig = {
        rowCount: 15,
        pageNumber: 1,
        idField: 'id',
        columns: [{ name: 'Number', mapping: 'id' }],
    };
    
    // FORM OPTIONS
    const assignTypeOptions = [{ value: 'Blanket', label: 'Blanket' }, { value: 'Single', label: 'Single' }];
    const ackTypeOptions = [{ value: 'Corporate', label: 'Corporate' }, { value: 'Individual', label: 'Individual' }];
    const grantorTypeOptions = [{ value: 'He', label: 'He' }, { value: 'She', label: 'She' }, { value: 'They', label: 'They' }];
    const booleanOptions = [{ value: 'Y', label: 'Yes' }, { value: 'N', label: 'No' }];

    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);

    const { register, errors, reset, handleSubmit, getValues, setValue, formState } = useForm();
    const { get, post, put, response } = useFetch();

    // const [selectedLeases, setSelectedLeases] = useState<number[]>([]);
    const [countyOptions, setCountyOptions] = useState<{ value: string; label: string; }[]>([]);
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string; }[]>([]);
    const [pLeaseRecordings, setPLeaseRecordings] = useState<PagedResultSet<LeaseRecording>>(null);
    const [selectList, setSelectList] = useState<number[]>([]);
    const [currentSearchText, setCurrentSearchText] = useState<string>('');
    const [mergeListConfig, setMergeListConfig] = useState<ITableConfig>({ rowCount: 15, pageNumber: 1});
    const [assignorResults, setAssignorResults] = useState<PagedResultSet<OwnerSearchResult>>(null);
    const [assigneeResults, setAssigneeResults] = useState<PagedResultSet<OwnerSearchResult>>(null);
    const [selectedAssignor, setSelectedAssignor] = useState<LeaseGenerationOwnerData>(null);
    const [selectedAssignee, setSelectedAssignee] = useState<LeaseGenerationOwnerData>(null);
    const [assignorLastPage, setAssignorLastPage] = useState<boolean>(false);
    const [assigneeLastPage, setAssigneeLastPage] = useState<boolean>(false);
    const [isNewAssignee, setIsNewAssignee] = useState<boolean>(true);
    const [templateList, setTemplateList] = useState<DocTemplateList>(undefined);
    const [selectedTemplate, setSelectedTemplate] = useState<number>(0);

    // Filter props
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [filterOptions, setFilterOptions] = useState<ODataOptions>(ODataOptions.from(parentListTableConfig));

    // USE EFFECTS
    useEffect(() => {
        getDocumentTemplates();

        if (!!props.assignment) {
            let form = finalizeData(props.assignment, true);
            reset(form);
            if (props.assignment.leaseRecordings?.length > 0) {
                let recIds = props.assignment.leaseRecordings.map(lr => lr.lr_tg);
                setSelectList(recIds);
            }

            if (props.states?.length > 0 && props.assignment.state?.length > 0) {
                getCounties(props.assignment.state);
            }

            if (props.assignment.assignor_lookup > 0) {
                getOwnerDetail(props.assignment.assignor_lookup, false);
            }

            if (props.assignment.assignee_lookup > 0) {
                getOwnerDetail(props.assignment.assignee_lookup, true);
            }
        }
    }, []);

    useEffect(() => {
        let lastPage = false;
        if (assignorResults) {
            lastPage = assignorResults.pageNumber * assignorResults.pageSize < assignorResults.totalRecords;
        }
        setAssignorLastPage(lastPage);
    }, [assignorResults]);

    useEffect(() => {
        let lastPage = false;
        if (assigneeResults) {
            lastPage = assigneeResults.pageNumber * assigneeResults.pageSize < assigneeResults.totalRecords;
        }
        setAssigneeLastPage(lastPage);
    }, [assigneeResults]);

    useEffect(() => {
        if (selectedAssignor !== null && selectedAssignor !== undefined) {
            let aDetail = selectedAssignor.ownerDetails;
            let stringDetail = OwnerUtil.MapOwner(aDetail).ownerName() + '\n';
            if (aDetail.address != null && aDetail.address !== undefined) {
                stringDetail += aDetail.address.addressLine1 + (aDetail.address.addressLine2?.length > 0 ? ', ' + aDetail.address.addressLine2 : '') + '\n';
                stringDetail += aDetail.address.city + ', ' + aDetail.address.state?.name + ' ' + aDetail.address.zipCode;
            }
            if (!props.assignment) {
                setValue('assignor', stringDetail);
            }
        }
    }, [selectedAssignor]);

    useEffect(() => {
        if (selectedAssignee !== null && selectedAssignee !== undefined) {
            let aDetail = selectedAssignee.ownerDetails;
            let stringDetail = OwnerUtil.MapOwner(aDetail).ownerName() + '\n';
            if (aDetail.address != null && aDetail.address !== undefined) {
                stringDetail += aDetail.address.addressLine1 + (aDetail.address.addressLine2?.length > 0 ? ', ' + aDetail.address.addressLine2 : '') + '\n';
                stringDetail += aDetail.address.city + ', ' + aDetail.address.state?.name + ' ' + aDetail.address.zipCode;
            }
            setValue('assignee', stringDetail);
        }
    }, [selectedAssignee]);

    useEffect(() => {
        if (props.states?.length > 0) {
            let stateSelect = props.states.map(s => { return { value: s.abbreviation, label: s.abbreviation }; });
            setStateOptions(stateSelect);
        }
    }, [props.states]);

    useEffect(() => {
        if (selectedAssignor !== null && selectedAssignor !== undefined) {
            getLeaseRecordings();
        }
    }, [filterOptions]);

    const onIncrementPage = () => {
        if (pageNumber * pLeaseRecordings.pageSize < pLeaseRecordings.totalRecords) {
            let pConfig = parentListTableConfig;
            pConfig.pageNumber = pageNumber + 1;
            
            let initOptions = ODataOptions.from(pConfig);
            
            initOptions.merge(initOptions);
            setFilterOptions(initOptions);
            setPageNumber(pConfig.pageNumber);
        }
    };

    // FETCH CALLS
    const getLeaseRecordings = async () => {
        let assignNo = props.assignment?.assign_no || 0;
        let isTransferred = props.assignment?.ownerships_transferred || false;

        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease-recording/by-owner/${selectedAssignor?.ownerDetails?.ownerLookUpId}/assigned/${assignNo}/${isTransferred}${filterOptions.toString()}`);

        if (response.ok) {
            let newRecordings = result;
            if (pageNumber > 1) {
                newRecordings.items = [...pLeaseRecordings.items].concat(result.items);
            }
            setPLeaseRecordings(newRecordings);
        } else {
            console.log('getLeaseRecordings Error:', result);
            window.alert('Error getting Lease Recordings');
        }
    };

    const getCounties = async (stateName: string) => {
        var state = props.states.find(s => s.abbreviation === stateName);

        if (state !== undefined) {
            let result: PagedResultSet<County> = await get(`/v1.0/states/${state.id}/counties`);
    
            if (response.ok) {
                if (result?.items !== undefined) {
                    let countySelect = result.items.map(c => { return { value: c.name, label: c.name }; });
                    setCountyOptions(countySelect);
                }    
            } else {
                console.log('getCounties Error:', result);
                window.alert('Error getting Counties');
            }    
        }
    };

    const renderDocTemplate = async () => {
        const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/assignment/${props.assignment.assign_no}/merge/${selectedTemplate}`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_mergedDoc`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = 'Assignment Exhibit';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            console.log('Unable to generate document.', result);
        }
    };

    const getOwnerSearchResults = async (options: ODataOptions, isAssignee: boolean) => {
        // return this.getRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${projectNumber}/owner-search/${searchText}/${ownerType}`, jwt);
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owner-search${options.toString()}`);

        if (response.ok) {
            if (isAssignee) {
                setAssigneeResults(result);
            } else {
                setAssignorResults(result);
            }
        }
    };

    const getOwnerDetail = async (ownerId: number, forAssignee: boolean) => {
        if (ownerId > 0) {
            const result: LeaseGenerationOwnerData = await get(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${ownerId}`
            );
    
            if (response.ok) {
                if (forAssignee) {
                    setSelectedAssignee(result);
                } else {
                    setSelectedAssignor(result);
                }
            }
        }
    };
    
    const getDocumentTemplates = async () => {
        let result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/by-project/${Constants.documentTemplateTypes.ASSIGNMENT}`);

        if (response.ok && result?.length > 0) {
            let templates = result.map((tl: DocumentTemplate) => {
                return { id: tl.referenceNumber, name: tl.name };
            });

            let tList: DocTemplateList = { id: 5, name: Constants.documentTemplateTypes.ASSIGNMENT, templates: templates };

            setTemplateList(tList);
        }
    };

    const getExhibit = async () => {
        await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/assignment/${props.assignment.assign_no}/get-exhibit`);

        if (response.ok) {
            await response.blob().then(blob => {
                if (!blob || blob.type !== 'application/octet-stream') {
                    console.log('Unexpected file response:', blob);
                    return;
                }
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Assignment_Exhibit_${props.assignment.assign_no}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    const createAssignment = async (assignment: Assignment, transferOwnership: boolean) => {
        let result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/assignment`, assignment);

        if (response.ok) {
            if (transferOwnership) {
                updateOwnerships(assignment);
            } else {
                props.sendUpdate();
                props.onCloseDialog();
            }
        } else {
            console.log('createAssignment Error:', result);
            window.alert('Error creating Assignment');
        }
    };

    const updateAssignment = async (assignment: Assignment, transferOwnership: boolean) => {
        let result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/assignment`, assignment);

        if (response.ok) {
            if (transferOwnership) {
                updateOwnerships(assignment);
            } else {
                props.sendUpdate();
                props.onCloseDialog();
            }
        } else {
            console.log('updateLeaseRecording Error:', result);
            window.alert('Error updating Assignment');
        }
    };

    const updateOwnerships = async (assignment: Assignment) => {
        let result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/assignment/wi-owner`, assignment);

        if (response.ok) {
            props.sendUpdate();
            props.onCloseDialog();
        } else {
            console.log('updateOwnerships Error:', result);
            window.alert('Error transferring Ownerships!');
        }
    };

    // FORM LOGIC
    const handleRecordingSelect = (e: FormEvent<HTMLInputElement>, recordingId: number) => {
        let newList = [...selectList];

        let record = newList.indexOf(recordingId);

        if (e.currentTarget.checked === true && record === -1) {
            newList.push(recordingId);
        } else if (e.currentTarget.checked !== true && record > -1) {
            newList.splice(record, 1);
        }

        setSelectList(newList);
    };

    // FORM FILTER LOGIC
    const searchUpdate = _.debounce((searchText: string, forAssignee: boolean) => {
        setCurrentSearchText(searchText);
        const options = ODataOptions.from(mergeListConfig);
        if (searchText === undefined || searchText === '') {
            searchText = '_';
        } else {
            options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, searchText));
        }
        
        getOwnerSearchResults(options, forAssignee);
    }, 300);

    const incrementOwners = (forAssignee: boolean) => {
        let newConfig = mergeListConfig;
        newConfig.pageNumber = mergeListConfig.pageNumber + 1;
        setMergeListConfig(newConfig);

        const options = ODataOptions.from(newConfig);
        if (currentSearchText !== undefined || currentSearchText !== '') {
            setCurrentSearchText('_');
        }
        options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, currentSearchText));

        getOwnerSearchResults(options, forAssignee);
    };

    // SUBMISSION LOGIC
    const onTransferOwnership = () => {
        let body = 'Are you sure you want to transfer Working Interest Ownership (this cannot be undone)?';
        let values = getValues();
        dialogContext.common.confirmAction('Transfer Ownership', body, () => onSubmit(values as Assignment, true));
    };

    const onSubmit = (data: Assignment, transferOwnership: boolean) => {
        if (selectList.length > 0) {
            data = finalizeData(data, false);
            if (data.assign_no > 0) {
                updateAssignment(data, transferOwnership);
            } else {
                createAssignment(data, transferOwnership);
            }
        } else {
            window.alert('At least one lease record must be attached!');
        }
    };

    const finalizeData = (data: Assignment, keepList: boolean) => {
        data.assign_no = props.assignment ? props.assignment.assign_no : 0;
        data.assignor_lookup = data.assignor_lookup ? data.assignor_lookup : selectedAssignor?.ownerDetails?.ownerLookUpId;
        data.assignee_lookup = data.assignee_lookup ? data.assignee_lookup : selectedAssignee?.ownerDetails?.ownerLookUpId;

        data.comm_date = data.comm_date ? (moment(data.comm_date).format('YYYY-MM-DD') as unknown as Date) : null;
        data.exe_date = data.exe_date ? (moment(data.exe_date).format('YYYY-MM-DD') as unknown as Date) : null;
        data.doc_date = data.doc_date ? (moment(data.doc_date).format('YYYY-MM-DD') as unknown as Date) : null;
        data.rec_date = data.rec_date ? (moment(data.rec_date).format('YYYY-MM-DD') as unknown as Date) : null;

        data.decimal_interest = data.decimal_interest ? parseFloat(data.decimal_interest + '') : null;

        if (keepList) {
            data.leaseRecordings = props.assignment?.leaseRecordings ?? [];
        } else {
            let oldList = props.assignment?.leaseRecordings ?? [];
            data.detachRecordings = oldList.filter(ol => selectList.indexOf(ol.lr_tg) === -1);
            data.leaseRecordings = pLeaseRecordings?.items?.filter(pl => selectList.indexOf(pl.lr_tg) > -1);
        }

        data.ownerships_transferred = props.assignment?.ownerships_transferred || false;

        return data;
    };

    // UI DISPLAY
    const bookOptions = () => {
        return [{ value: 'Y', label: 'Book/Page' }, { value: 'N', label: 'Instrument No.' }];
    };

    const displayOwnerSearchResults = (forAssignee: boolean) => {
        let ownerList = forAssignee ? assigneeResults : assignorResults;
        let sOwner = forAssignee ? selectedAssignee : selectedAssignor;

        if (ownerList?.items?.length > 0) {
            return ownerList.items.map((owner: OwnerSearchResult) => {
                return (
                    <Card
                        key={`owner-${owner.id}`}
                        onClick={() => getOwnerDetail(owner.id, forAssignee)}
                        badge={`Owner #${owner.id}`}
                        className={owner.id === sOwner?.ownerDetails?.ownerLookUpId ? 'selected' : ''}
                        style={{ marginTop: '1rem' }}
                    >
                        <div style={{ padding: '1rem' }}>
                            <div className="title">{owner.name}</div>
                            <div>{owner.type}</div>
                        </div>
                    </Card>
                );
            });
        } else {
            return <div>No Owners found</div>;
        }
    };

    const newAssignee = () => {
        const actions = [
            { id: 1, intent: 'primary', text: 'Create Owner', action: (): void => undefined, isSave: true },
            { id: 3, intent: 'danger', text: 'Cancel', action: props.onCloseDialog, isSave: false },
        ];

        return <CreateOwner onOwnerCreated={(owner: IOwner) => getOwnerDetail(owner.id, true)} actions={actions} />;
    };

    const handleFilterChanged = (options: ODataOptions) => {
        setPageNumber(1);
        let initOptions = ODataOptions.from(parentListTableConfig);

        initOptions.merge(options);
        setFilterOptions(initOptions);
    };

    return (
        <Dialog title="Assignments">
            <div className="mb-1 mt-1" style={{ width: '85vw' }}>
                <form onSubmit={handleSubmit((formValues: Assignment) => onSubmit(formValues, false))}>
                    <div style={{ height: '80vh', marginBottom: '1rem', overflow: 'auto' }}>
                        {!(props.assignment && props.assignment.ownerships_transferred === true) && (
                            <CollapsibleSection title="Assignee" startCollapsed={true}>
                                <div style={{ display: 'flex' }}>
                                    <input type="radio" id="ae_new" name="assigneeStatus" defaultChecked={true} onClick={() => setIsNewAssignee(true)} />
                                    <label htmlFor="ae_new">New Owner</label>

                                    <div style={{ margin: '0 1rem' }}>|</div>

                                    <input type="radio" id="ae_ex" name="assigneeStatus" onClick={() => setIsNewAssignee(false)} />
                                    <label htmlFor="ae_ex">Existing</label>
                                </div>
                                <Page style={{ display: 'flex', clear: 'both', width: '100%' }}>
                                    <Page.Section>
                                        {!isNewAssignee && (
                                            <>
                                                <div>Select Assignee</div>
                                                <RHFInputBox
                                                    name="searchText"
                                                    label="Owner Search by Name"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => searchUpdate(e.target.value, true)}
                                                />
                                                <div className="dlc-content-panel-header">Owners</div>
                                                <CardContainer 
                                                    showNextPagePanel={assigneeLastPage}
                                                    nextPageAction={() => incrementOwners(true)}
                                                >
                                                    {displayOwnerSearchResults(true)}
                                                </CardContainer>                            
                                            </>
                                        )}
                                        {isNewAssignee && (
                                            newAssignee()
                                        )}
                                        <br />
                                        {selectedAssignee && (
                                            <span>Assignee Created!</span>
                                        )}
                                    </Page.Section>
                                </Page>
                            </CollapsibleSection>
                        )}
                        {!props.assignment && (
                            <CollapsibleSection title="Assignor" startCollapsed={true}>
                                <div style={{ display: 'flex' }}>
                                    <Page style={{ display: 'flex', clear: 'both', width: '100%' }}>
                                        <Page.Section>
                                            <>
                                                <div>Select Assignor</div>
                                                <RHFInputBox
                                                    name="searchText"
                                                    label="Owner Search by Name"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => searchUpdate(e.target.value, false)}
                                                />
                                                <div className="dlc-content-panel-header">Owners</div>
                                                <CardContainer 
                                                    showNextPagePanel={assignorLastPage}
                                                    nextPageAction={() => incrementOwners(false)}
                                                >
                                                    {displayOwnerSearchResults(false)}
                                                </CardContainer>                            
                                            </>
                                        </Page.Section>
                                    </Page>
                                </div>
                            </CollapsibleSection>
                        )}
                        <CollapsibleSection title="Assignment Data">
                            <div style={{ display: 'flex', justifyContent: 'space-around', rowGap: '1rem' }}>
                                <RHFInputBox
                                    name="assign_no"
                                    label="Assign no"
                                    register={register}
                                    errors={errors}
                                    disabled={true}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFSelect
                                    name="assign_type"
                                    label="Type"
                                    options={assignTypeOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFSelect
                                    name="ack_type"
                                    label="Acknowledge"
                                    options={ackTypeOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFSelect
                                    name="state"
                                    label="State"
                                    options={stateOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '5%' }}
                                    style={{ width: '100%' }}
                                    onChange={(e) => getCounties(e.currentTarget.value)}
                                />

                                <RHFSelect
                                    name="county"
                                    label="County"
                                    options={countyOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                    disabled={countyOptions.length === 0}
                                />

                                <RHFSelect
                                    name="grant_type"
                                    label="Grantor Type"
                                    options={grantorTypeOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '5%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="exe_date"
                                    label="Exe. Date"
                                    register={register}
                                    errors={errors}
                                    type="date"
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="notary"
                                    label="Notary"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="phone"
                                    label="Phone"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <br />
                            <div style={{ display: 'flex', justifyContent: 'space-around', rowGap: '1rem' }}>
                                <RHFInputBox
                                    name="comm_date"
                                    label="Comm. Date"
                                    register={register}
                                    errors={errors}
                                    type="date"
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="appeared"
                                    label="Appeared"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="title"
                                    label="Title"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="sign_company"
                                    label="Sig. Comp"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="sign_name"
                                    label="Sig. Name"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="doc_date"
                                    label="Report Date"
                                    register={register}
                                    errors={errors}
                                    type="date"
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <br />
                            <div style={{ display: 'flex', justifyContent: 'space-around', rowGap: '1rem' }}>
                                <RHFSelect
                                    name="recorded"
                                    label="Recorded"
                                    options={booleanOptions}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="rec_date"
                                    label="Rec. Date"
                                    register={register}
                                    errors={errors}
                                    type="date"
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFSelect
                                    label="Instrument Type"
                                    name="use_book"
                                    options={bookOptions()}
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '10%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="book"
                                    label="Book/Doc No."
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />

                                <RHFInputBox
                                    name="page"
                                    label="Page"
                                    register={register}
                                    errors={errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <br />
                            <div style={{ display: 'flex', justifyContent: 'space-around', rowGap: '1rem' }}>
                                <RHFTextArea
                                    name="assignor"
                                    label="Assignor"
                                    register={register}
                                    style={{ width: '100%', maxWidth: '100%', height: '10rem' }}
                                    bodyStyle={{ width: '48%'}}
                                />

                                <RHFTextArea
                                    name="assignee"
                                    label="Assignee"
                                    register={register}
                                    style={{ width: '100%', maxWidth: '100%', height: '10rem' }}
                                    bodyStyle={{ width: '48%'}}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', rowGap: '1rem' }}>
                                <RHFInputBox
                                    label="Interest (By Percent of the Assignor's Working Interest)"
                                    name="decimal_interest"
                                    type="number"
                                    register={register}
                                    errors={errors}
                                    disabled={props.assignment?.ownerships_transferred}
                                    rules={{ min: { value: 0.0001, message: 'Must be a positive value!' }, max: {value: 1, message: 'Must be less than or equal to one!'} }}
                                    style={{ width: '100%' }}
                                />

                            </div>
                            <Button intent="primary" onClick={onTransferOwnership} text="Assign Ownership" disabled={props.assignment?.ownerships_transferred} />
                            {props.assignment?.ownerships_transferred && (
                                <span>Ownerships updated!</span>
                            )}
                            {!props.assignment?.ownerships_transferred && (
                                <span style={{ color: 'red' }}>This cannot be undone.</span>
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Lease Recordings" startCollapsed={true}>
                            {selectedAssignor !== null && selectedAssignor !== undefined && (
                                <>
                                    <div>
                                        <div className="dlc-content-panel-header">Recorded Leases</div>
                                        <Filter
                                            sortItems={[
                                                { group: 1, text: 'Lease No', icon: 'alpha-down', orderName: 'lm.LS_NO', orderDirection: 'descending' },
                                                { group: 1, text: 'Invoice No', icon: 'alpha-down', orderName: 'li.INVOICE_NO1', orderDirection: 'descending' },
                                                { group: 1, text: 'Assign No', icon: 'alpha-down', orderName: 'lr.ASSIGN_NO', orderDirection: 'descending' },
                                                { group: 1, text: 'Lessor Name', icon: 'alpha-down', orderName: 'll.NAME1', orderDirection: 'descending' },
                                            ]}
                                            filterItems={[
                                                { group: 1, text: 'Lease No', orderName: 'lease_no', orderDirection: 'descending' },
                                                { group: 1, text: 'Invoice No', orderName: 'invoice_no', orderDirection: 'descending' },
                                                { group: 1, text: 'Assign No', orderName: 'assign_no', orderDirection: 'descending' },
                                                { group: 1, text: 'Lessor Name', orderName: 'lessor_name', orderDirection: 'descending' },
                                            ]}
                                            onFilterChanged={handleFilterChanged}
                                        />
                                    </div>
                                    {pLeaseRecordings?.items?.length > 0 && (
                                        <LeaseRecordingsList
                                            leaseRecordings={pLeaseRecordings}
                                            forBatching={true}
                                            isAssigned={props.assignment?.ownerships_transferred}
                                            selectList={selectList}
                                            updateSelectList={handleRecordingSelect}
                                            onUpdateLeaseRecording={props.sendUpdate}
                                            incrementPage={onIncrementPage}
                                        />
                                    )}
                                    {pLeaseRecordings?.items?.length === 0 && <LoadingSpinner />}
                                </>
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Generate Exhibit" startCollapsed={true}>
                            {(templateList?.templates?.length > 0 && !!props.assignment) && (
                                <div style={{ display: 'flex', marginTop: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                                    <DocGenerationComponent
                                        customDoc={templateList}
                                        disabled={!props.assignment || selectedTemplate === 0}
                                        setTemplateId={setSelectedTemplate}
                                        renderDocTemplate={renderDocTemplate}
                                    />
                                    <a style={{ display: 'hidden' }} id="a_mergedDoc" />
                                </div>
                            )}
                            {(!templateList?.templates && templateList?.templates.length === 0) && (
                                <span>Please create an Assignment template in Settings.</span>
                            )}
                            {(!props.assignment || formState.isDirty) && (
                                <span>Please save changes to Assignment before generating exhibit.</span>
                            )}
                            <Button intent="primary" onClick={getExhibit} text="Generate Spreadsheet" />
                        </CollapsibleSection>
                    </div>
                    <DialogActionBar>
                        <Button
                            intent="primary"
                            type="submit"
                            text="Save"
                        />
                        <Button intent="danger" onClick={() => props.onCloseDialog()} text="Cancel" />
                    </DialogActionBar>
                </form>
            </div>
        </Dialog>
    );
};

export default AssignmentDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ProcessingAndMaintenance/AssignmentDialog.tsx